import { useEffect, useRef } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { ThemeMode, useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  AlarmIcon as DarkAlarmIcon,
  Alert as DarkAlert,
  CameraIcon as DarkCameraIcon,
  DoorIcon as DarkDoorIcon,
  TraceLogEvent,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'

import { DeviceType, EventType } from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'

import { EventsT } from '../../../shared/types/traceLogTypes'
import {
  getDisplayBadgeNumber,
  getEventName,
} from '../../../shared/utils/traceLogUtils'

interface EventItemIProps {
  event: EventsT
  isCauseOfIncident?: boolean
  isHighlightedFromResolution?: boolean
  isFromResolvedAction?: boolean
  shouldShowDeviceInfo?: boolean
}

export const EventItem = ({
  event,
  isCauseOfIncident,
  isHighlightedFromResolution,
  isFromResolvedAction,
  shouldShowDeviceInfo,
}: EventItemIProps) => {
  const {
    detectedAt,
    type,
    cardHolderName,
    badgeNumber,
    deviceName,
    deviceType,
  } = event

  const { t } = useTranslation()
  const isTraceLogsPiiEnabled = useFeatureFlag(FeatureFlag.traceLogsPii)
  const mode = useLightMode()
  const isHighlightedFromIncident =
    isCauseOfIncident && type !== EventType.DoorUnlocked
  const isHighlighted = isHighlightedFromResolution || isHighlightedFromIncident
  const ref = useRef<HTMLDivElement>(null)
  const hasDeviceInfo = shouldShowDeviceInfo && deviceName
  const shouldShowTailgateAlert =
    isCauseOfIncident && type === EventType.DoorUnlocked

  useEffect(() => {
    if (isHighlighted && ref?.current) {
      ref.current?.scrollIntoView({ behavior: 'auto', block: 'center' })
    }
  }, [isHighlighted])

  const hasCallout = isFromResolvedAction || shouldShowTailgateAlert
  const callout = hasCallout ? (
    <>
      {isFromResolvedAction && (
        <DarkAlert
          colorScheme={mode}
          description={t('incidents:drawer_trace_log_resolution_alert')}
          size='compact'
          variant='success'
        />
      )}
      {shouldShowTailgateAlert && (
        <DarkAlert
          colorScheme={mode}
          description={t('incidents:drawer_trace_log_tailgate_alert')}
          size='compact'
          variant='info'
        />
      )}
    </>
  ) : undefined

  return (
    <TraceLogEvent
      // Events without a badge omit the credential row entirely (ENG-32092,
      // superseding the ENG-31461 '0' placeholder). The PII flag still hides
      // it regardless.
      badgeNumber={
        isTraceLogsPiiEnabled ? getDisplayBadgeNumber(badgeNumber) : undefined
      }
      callout={callout}
      cardHolderName={
        isTraceLogsPiiEnabled && cardHolderName ? cardHolderName : undefined
      }
      colorScheme={mode}
      data-testid='traceLog_eventItem'
      deviceIcon={
        hasDeviceInfo
          ? getDarkEventDeviceIcon(deviceType, mode, isHighlightedFromIncident)
          : undefined
      }
      deviceName={hasDeviceInfo ? deviceName : undefined}
      ref={ref}
      timestamp={formatDateTime(detectedAt)}
      title={getEventName(event)}
      variant={isHighlightedFromIncident ? 'alarm' : 'default'}
    />
  )
}

export const getDarkEventDeviceIcon = (
  type: DeviceType,
  mode: ThemeMode,
  isAlarm = false
) => {
  // Match the detail-row text color the TraceLogEvent rows use — including
  // the error tint on alarm rows, so the device icon doesn't stay gray while
  // the rest of the row turns red.
  const prefix = tokenPrefix(mode)
  const color = tokenVar(
    `colors.${prefix}${isAlarm ? 'status.error-text' : 'text.secondary'}`
  )
  switch (type) {
    case DeviceType.Alarm:
      return <DarkAlarmIcon color={color} size={16} />
    case DeviceType.Camera:
      return <DarkCameraIcon color={color} size={16} />
    default:
      return <DarkDoorIcon color={color} size={16} />
  }
}
